import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const RCMConsulting = () => {
  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "Revenue Cycle Management Consulting Services",
    "description": "Expert revenue cycle management consulting to optimize your healthcare organization's financial performance. Reduce costs, improve collections, and accelerate cash flow.",
    "provider": {
      "@type": "MedicalBusiness",
      "name": "MDeRCM",
      "url": "https://mdercm.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Five Greentree Centre, 525 Route 73 North, Suite 104",
        "addressLocality": "Marlton",
        "addressRegion": "NJ",
        "postalCode": "08053",
        "addressCountry": "US"
      }
    },
    "areaServed": "United States"
  };

  const consultingServices = [
    {
      icon: '📊',
      title: 'RCM Assessment & Analysis',
      description: 'Comprehensive evaluation of your current revenue cycle processes, identifying bottlenecks, inefficiencies, and opportunities for improvement.',
      benefits: ['Gap Analysis', 'Process Mapping', 'Benchmarking', 'ROI Projections']
    },
    {
      icon: '🎯',
      title: 'Denial Management Strategy',
      description: 'Develop and implement strategies to reduce claim denials, improve appeal success rates, and prevent future denials.',
      benefits: ['Root Cause Analysis', 'Prevention Plans', 'Appeal Strategies', 'Staff Training']
    },
    {
      icon: '💰',
      title: 'Revenue Optimization',
      description: 'Identify and capture lost revenue opportunities through improved coding, charge capture, and collection processes.',
      benefits: ['Charge Capture Review', 'Coding Optimization', 'Underpayment Detection', 'Collection Strategies']
    },
    {
      icon: '🤖',
      title: 'Technology Integration',
      description: 'Select, implement, and optimize RCM technology solutions including AI tools, automation platforms, and analytics systems.',
      benefits: ['Vendor Selection', 'Implementation Support', 'Integration Planning', 'Staff Training']
    },
    {
      icon: '📈',
      title: 'Performance Improvement',
      description: 'Establish KPIs, implement monitoring systems, and create continuous improvement programs for sustained results.',
      benefits: ['KPI Development', 'Dashboard Creation', 'Performance Tracking', 'Improvement Plans']
    },
    {
      icon: '👥',
      title: 'Staff Training & Development',
      description: 'Comprehensive training programs for billing, coding, and revenue cycle staff to improve skills and productivity.',
      benefits: ['Custom Curriculum', 'Hands-on Training', 'Certification Prep', 'Ongoing Support']
    }
  ];

  const whyChoose = [
    {
      title: 'Industry Expertise',
      description: '20+ years combined experience in healthcare revenue cycle management across hospitals, clinics, and health systems.',
      icon: '🎓'
    },
    {
      title: 'Proven Results',
      description: 'Average 35% improvement in collection rates and 40% reduction in denials for our consulting clients.',
      icon: '📊'
    },
    {
      title: 'Customized Solutions',
      description: 'No cookie-cutter approaches - we tailor strategies to your organization\'s unique needs and challenges.',
      icon: '🎯'
    },
    {
      title: 'Hands-On Support',
      description: 'We don\'t just provide recommendations - we help implement changes and ensure sustainable results.',
      icon: '🤝'
    }
  ];

  const processSteps = [
    { step: '1', title: 'Discovery & Assessment', description: 'Comprehensive review of current state, challenges, and goals' },
    { step: '2', title: 'Analysis & Strategy', description: 'Data analysis, benchmarking, and strategic roadmap development' },
    { step: '3', title: 'Implementation', description: 'Execute improvements with hands-on support and change management' },
    { step: '4', title: 'Monitoring & Optimization', description: 'Track results, refine processes, and ensure sustainable success' }
  ];

  return (
    <>
      <Helmet>
        <title>RCM Consulting Services | Expert RCM Consultants | MDeRCM</title>
        <meta 
          name="description" 
          content="Expert RCM consulting services. Improve collections 35%, reduce denials 40%. Custom strategy & implementation for practices and hospitals."
        />
        <meta name="keywords" content="revenue cycle management consulting, RCM consulting services, revenue cycle consulting, healthcare consulting, RCM consultant" />
        <link rel="canonical" href="https://mdercm.com/revenue-cycle-consulting" />
        
        <meta property="og:title" content="Revenue Cycle Management Consulting | MDeRCM" />
        <meta property="og:description" content="Expert RCM consulting - Improve collections 35%, reduce denials 40%. Customized strategies." />
        <meta property="og:url" content="https://mdercm.com/revenue-cycle-consulting" />
        
        <script type="application/ld+json">
          {JSON.stringify(pageSchema)}
        </script>
      </Helmet>

      <main style={{ fontFamily: "'Poppins', sans-serif" }}>
        {/* Hero */}
        <section style={{
          background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
          padding: '120px 5% 80px',
          color: '#ffffff'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 style={{
                fontSize: 'clamp(2.2rem, 5vw, 3.5rem)',
                fontWeight: '800',
                marginBottom: '25px',
                lineHeight: '1.2'
              }}>
                Revenue Cycle Management Consulting Services
              </h1>
              <h2 style={{
                fontSize: 'clamp(1.3rem, 3vw, 2rem)',
                fontWeight: '600',
                marginBottom: '20px',
                color: '#FFD700'
              }}>
                Transform Your Financial Performance with Expert Guidance
              </h2>
              <p style={{
                fontSize: '1.2rem',
                lineHeight: '1.8',
                maxWidth: '800px',
                marginBottom: '35px'
              }}>
                Partner with experienced <strong>revenue cycle management consultants</strong> who deliver measurable results. We help healthcare organizations optimize processes, reduce costs, and maximize revenue.
              </p>
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <Link to="/free-trial" style={{
                  display: 'inline-block',
                  padding: '15px 35px',
                  backgroundColor: '#F26522',
                  color: '#ffffff',
                  borderRadius: '30px',
                  textDecoration: 'none',
                  fontWeight: '700',
                  fontSize: '1.1rem',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d3541c'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#F26522'}
                >
                  Get Free Assessment
                </Link>
     
              
              </div>
            </motion.div>
          </div>
        </section>

        {/* Stats */}
        <section style={{ padding: '60px 5%', backgroundColor: '#f8f9fa' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '30px',
              textAlign: 'center'
            }}>
              {[
                { number: '200+', label: 'Consulting Projects', icon: '📊' },
                { number: '35%', label: 'Avg. Collection Improvement', icon: '💰' },
                { number: '40%', label: 'Denial Reduction', icon: '✅' },
                { number: '20+ Years', label: 'Industry Experience', icon: '🎓' }
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  style={{
                    padding: '30px 20px',
                    backgroundColor: '#ffffff',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                  }}
                >
                  <div style={{ fontSize: '3rem', marginBottom: '10px' }}>{stat.icon}</div>
                  <div style={{ fontSize: '2.5rem', fontWeight: '800', color: '#2e7d32', marginBottom: '5px' }}>
                    {stat.number}
                  </div>
                  <div style={{ fontSize: '1rem', color: '#666', fontWeight: '500' }}>
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Consulting Services */}
        <section style={{ padding: '80px 5%', backgroundColor: '#ffffff' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: 'clamp(2rem, 4vw, 2.8rem)',
              fontWeight: '700',
              textAlign: 'center',
              marginBottom: '20px',
              color: '#1A1A1A'
            }}>
              Our RCM Consulting Services
            </h2>
            <p style={{
              fontSize: '1.1rem',
              textAlign: 'center',
              maxWidth: '800px',
              margin: '0 auto 50px',
              color: '#666'
            }}>
              Comprehensive <strong>revenue cycle management consulting</strong> services tailored to your organization's needs.
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
              gap: '35px'
            }}>
              {consultingServices.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  style={{
                    padding: '35px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '12px',
                    border: '2px solid #e9ecef',
                    transition: 'all 0.3s ease'
                  }}
                  whileHover={{
                    borderColor: '#2e7d32',
                    boxShadow: '0 10px 30px rgba(46,125,50,0.15)',
                    transform: 'translateY(-5px)'
                  }}
                >
                  <div style={{ fontSize: '3.5rem', marginBottom: '15px' }}>{service.icon}</div>
                  <h3 style={{ fontSize: '1.4rem', fontWeight: '700', marginBottom: '12px', color: '#2e7d32' }}>
                    {service.title}
                  </h3>
                  <p style={{ fontSize: '1rem', color: '#555', lineHeight: '1.7', marginBottom: '20px' }}>
                    {service.description}
                  </p>
                  <div style={{
                    backgroundColor: '#ffffff',
                    padding: '15px',
                    borderRadius: '8px',
                    border: '1px solid #e9ecef'
                  }}>
                    <strong style={{ color: '#2e7d32', fontSize: '0.9rem' }}>What's Included:</strong>
                    <ul style={{
                      margin: '10px 0 0 0',
                      padding: '0 0 0 20px',
                      fontSize: '0.95rem',
                      color: '#666',
                      lineHeight: '1.8'
                    }}>
                      {service.benefits.map((benefit, idx) => (
                        <li key={idx}>{benefit}</li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Process */}
        <section style={{ padding: '80px 5%', backgroundColor: '#f8f9fa' }}>
          <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: 'clamp(2rem, 4vw, 2.8rem)',
              fontWeight: '700',
              textAlign: 'center',
              marginBottom: '50px',
              color: '#1A1A1A'
            }}>
              Our Consulting Process
            </h2>

            <div style={{ position: 'relative' }}>
              {processSteps.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.2 }}
                  viewport={{ once: true }}
                  style={{
                    display: 'flex',
                    gap: '30px',
                    marginBottom: index < processSteps.length - 1 ? '40px' : '0',
                    alignItems: 'flex-start'
                  }}
                >
                  <div style={{
                    width: '70px',
                    height: '70px',
                    borderRadius: '50%',
                    backgroundColor: '#2e7d32',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '2rem',
                    fontWeight: '800',
                    flexShrink: 0,
                    boxShadow: '0 4px 12px rgba(46,125,50,0.3)'
                  }}>
                    {item.step}
                  </div>
                  <div style={{
                    flex: 1,
                    padding: '15px 25px',
                    backgroundColor: '#ffffff',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                  }}>
                    <h3 style={{ fontSize: '1.4rem', fontWeight: '700', marginBottom: '10px', color: '#2e7d32' }}>
                      {item.title}
                    </h3>
                    <p style={{ fontSize: '1rem', color: '#666', lineHeight: '1.7' }}>
                      {item.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose */}
        <section style={{ padding: '80px 5%', backgroundColor: '#ffffff' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: 'clamp(2rem, 4vw, 2.8rem)',
              fontWeight: '700',
              textAlign: 'center',
              marginBottom: '50px',
              color: '#1A1A1A'
            }}>
              Why Choose MDeRCM Consulting?
            </h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '30px'
            }}>
              {whyChoose.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  style={{
                    padding: '35px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '12px',
                    textAlign: 'center',
                    transition: 'all 0.3s ease'
                  }}
                  whileHover={{
                    backgroundColor: '#e8f5e9',
                    transform: 'translateY(-5px)'
                  }}
                >
                  <div style={{ fontSize: '4rem', marginBottom: '20px' }}>{item.icon}</div>
                  <h3 style={{ fontSize: '1.3rem', fontWeight: '700', marginBottom: '12px', color: '#2e7d32' }}>
                    {item.title}
                  </h3>
                  <p style={{ fontSize: '1rem', color: '#666', lineHeight: '1.7' }}>
                    {item.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section style={{
          padding: '80px 5%',
          background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
          color: '#ffffff',
          textAlign: 'center'
        }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: 'clamp(2rem, 4vw, 2.8rem)',
              fontWeight: '700',
              marginBottom: '20px'
            }}>
              Ready to Optimize Your Revenue Cycle?
            </h2>
            <p style={{
              fontSize: '1.2rem',
              marginBottom: '35px',
              lineHeight: '1.8'
            }}>
              Get expert <strong>revenue cycle management consulting</strong> that delivers real results. Schedule your free assessment today and discover how we can help you achieve your financial goals.
            </p>
            <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/free-trial" style={{
                display: 'inline-block',
                padding: '18px 40px',
                backgroundColor: '#F26522',
                color: '#ffffff',
                borderRadius: '30px',
                textDecoration: 'none',
                fontWeight: '700',
                fontSize: '1.1rem',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d3541c'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#F26522'}
              >
                Schedule Free Assessment
              </Link>
              <a href="mailto:info@mdercm.com" style={{
                display: 'inline-block',
                padding: '18px 40px',
                backgroundColor: 'transparent',
                color: '#ffffff',
                border: '2px solid #ffffff',
                borderRadius: '30px',
                textDecoration: 'none',
                fontWeight: '700',
                fontSize: '1.1rem',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#ffffff';
                e.currentTarget.style.color = '#1b5e20';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#ffffff';
              }}
              >
                📧 Email Us
              </a>
            </div>
          </div>
        </section>

        {/* Related */}
        <section style={{ padding: '60px 5%', backgroundColor: '#f8f9fa' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h3 style={{
              fontSize: '1.8rem',
              fontWeight: '700',
              textAlign: 'center',
              marginBottom: '40px',
              color: '#1A1A1A'
            }}>
              Related Services
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '25px'
            }}>
              {[
                { title: 'Medical Billing Outsourcing', link: '/medical-billing-outsourcing', icon: '💼' },
                { title: 'Hospital RCM', link: '/hospital-revenue-cycle-management', icon: '🏥' },
                { title: 'AI Healthcare Solutions', link: '/healthcare/ai', icon: '🤖' },
                { title: 'Denial Management', link: '/healthcare/ai/denial-management', icon: '🔄' }
              ].map((item, index) => (
                <Link key={index} to={item.link} style={{
                  padding: '30px',
                  backgroundColor: '#ffffff',
                  borderRadius: '12px',
                  textAlign: 'center',
                  textDecoration: 'none',
                  color: '#333',
                  border: '2px solid #e9ecef',
                  transition: 'all 0.3s ease',
                  display: 'block'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#2e7d32';
                  e.currentTarget.style.transform = 'translateY(-5px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e9ecef';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                >
                  <div style={{ fontSize: '3rem', marginBottom: '15px' }}>{item.icon}</div>
                  <h4 style={{ fontSize: '1.2rem', fontWeight: '600', color: '#2e7d32' }}>
                    {item.title}
                  </h4>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default RCMConsulting;